/*
 * Keksi bundle-builder — "More / Less" expander for flavor descriptions.
 *
 * WHY THIS EXISTS
 * ---------------
 * The flavor description modal is rendered by the custom `keksi-bundle-builder`
 * app (not by the theme). The app TRUNCATES the description before putting it in
 * the DOM (e.g. "...baking soda/powder, salt,..."), so the full text is not
 * present on the page and cannot simply be "un-clamped".
 *
 * This theme-side enhancement re-fetches the full product description from
 * Shopify's own product JSON, matches it to the open modal BY TITLE, verifies
 * the fetched text starts with what the modal is already showing (so it can
 * never display the wrong product's text), and then renders it with a CSS line
 * clamp plus a "More / Less" toggle.
 *
 * SAFETY / SCOPE
 * --------------
 * - It only ever touches `.keksi-modal-desc` inside `.keksi-modal-overlay`.
 * - It only acts when the visible text is actually truncated (ends in "..."/"…").
 * - If the full text can't be fetched or fails verification, it does NOTHING and
 *   leaves the app's original text exactly as-is.
 * No other theme functionality is affected.
 */
(function () {
  'use strict';

  if (window.__keksiFlavorReadMore) return;
  window.__keksiFlavorReadMore = true;

  // ---- Config (safe to tweak) ---------------------------------------------
  var CONFIG = {
    overlaySelector: '.keksi-modal-overlay',
    descSelector: '.keksi-modal-desc',
    titleSelector: '.keksi-modal-title',
    clampLines: 4, // lines shown while collapsed
    moreLabel: 'More',
    lessLabel: 'Less',
  };

  // ---- One-time stylesheet -------------------------------------------------
  function injectStyles() {
    if (document.getElementById('keksi-flavor-readmore-styles')) return;
    var style = document.createElement('style');
    style.id = 'keksi-flavor-readmore-styles';
    style.textContent = [
      '.keksi-modal-desc.keksi-rm-collapsed .keksi-rm-text{',
      '  display:-webkit-box;-webkit-box-orient:vertical;',
      '  -webkit-line-clamp:' + CONFIG.clampLines + ';line-clamp:' + CONFIG.clampLines + ';',
      '  overflow:hidden;',
      '}',
      '.keksi-modal-desc .keksi-rm-toggle{',
      '  display:inline-block;margin-top:0px;padding:0;',
      '  background:none;border:0;font:inherit;font-weight:600;color:inherit;',
      '  text-decoration:underline;text-underline-offset:2px;',
      '  cursor:pointer;opacity:.85;',
      '}',
      '.keksi-modal-desc .keksi-rm-toggle:hover{opacity:1;}',
    ].join('');
    (document.head || document.documentElement).appendChild(style);
  }

  // ---- Text helpers --------------------------------------------------------
  function norm(s) {
    return (s || '').replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
  }
  function lc(s) {
    return norm(s).toLowerCase();
  }
  function htmlToText(html) {
    var d = document.createElement('div');
    d.innerHTML = html || '';
    return norm(d.textContent || d.innerText || '');
  }
  function slugify(title) {
    return norm(title)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // ---- Product description lookup -----------------------------------------
  var productsPromise = null; // Map<normalizedTitle, fullText>
  var handleCache = {}; // slug -> fullText (per-item fallback)

  function loadProducts() {
    if (productsPromise) return productsPromise;
    productsPromise = (async function () {
      var map = {};
      try {
        for (var page = 1; page <= 5; page++) {
          var res = await fetch(location.origin + '/products.json?limit=250&page=' + page, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
          if (!res.ok) break;
          var data = await res.json();
          var items = (data && data.products) || [];
          if (!items.length) break;
          for (var i = 0; i < items.length; i++) {
            var pr = items[i];
            if (pr && pr.title) map[lc(pr.title)] = htmlToText(pr.body_html);
          }
          if (items.length < 250) break;
        }
      } catch (e) {
        /* ignore — per-handle fallback below still works */
      }
      return map;
    })();
    return productsPromise;
  }

  async function fetchByTitle(title) {
    var slug = slugify(title);
    if (!slug) return '';
    if (Object.prototype.hasOwnProperty.call(handleCache, slug)) return handleCache[slug];
    var text = '';
    try {
      var res = await fetch(location.origin + '/products/' + slug + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (res.ok) {
        var d = await res.json();
        text = htmlToText(d.description || d.body_html || '');
      }
    } catch (e) {
      /* ignore */
    }
    handleCache[slug] = text;
    return text;
  }

  async function getFullText(title) {
    var key = lc(title);
    var map = await loadProducts();
    if (map[key]) return map[key];
    return fetchByTitle(title);
  }

  // ---- Core ----------------------------------------------------------------
  function endsTruncated(text) {
    return /(\.{3}|…)$/.test(text);
  }

  function injectReadMore(p, fullText, normTitle) {
    var span = document.createElement('span');
    span.className = 'keksi-rm-text';
    span.textContent = fullText;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'keksi-rm-toggle';
    btn.textContent = CONFIG.moreLabel;
    btn.setAttribute('aria-expanded', 'false');

    p.textContent = '';
    p.appendChild(span);
    p.appendChild(btn);
    p.classList.add('keksi-rm-collapsed');
    p.dataset.keksiRm = normTitle;

    btn.addEventListener('click', function () {
      var collapsed = p.classList.toggle('keksi-rm-collapsed');
      btn.textContent = collapsed ? CONFIG.moreLabel : CONFIG.lessLabel;
      btn.setAttribute('aria-expanded', String(!collapsed));
    });

    // Measure after layout: if the full text fits within the clamp there is
    // nothing to expand, so drop the clamp and the toggle entirely.
    requestAnimationFrame(function () {
      if (span.scrollHeight - span.clientHeight <= 2) {
        p.classList.remove('keksi-rm-collapsed');
        btn.remove();
      }
    });
  }

  async function processOne(p) {
    if (!p || p.dataset.keksiRmPending === '1') return;

    var overlay = p.closest(CONFIG.overlaySelector) || p.parentElement;
    var titleEl = overlay && overlay.querySelector(CONFIG.titleSelector);
    var title = titleEl ? titleEl.textContent : '';
    var normTitle = lc(title);

    if (!title) return;
    if (p.dataset.keksiRm === normTitle) return; // already handled for this flavor

    var raw = norm(p.textContent);
    if (!endsTruncated(raw)) return; // not truncated -> leave untouched

    p.dataset.keksiRmPending = '1';
    try {
      var full = await getFullText(title);
      if (!full || !p.isConnected) return;

      // Bail if the modal switched to another flavor while we were fetching.
      var nowTitleEl = overlay && overlay.querySelector(CONFIG.titleSelector);
      if (nowTitleEl && lc(nowTitleEl.textContent) !== normTitle) return;

      var shown = norm(p.textContent).replace(/[\s.,…]*$/, '');
      var fullL = lc(full);
      var shownL = lc(shown);
      if (fullL.length <= shownL.length) return;

      // Verification: fetched text must begin with what the modal shows, so we
      // can never inject a different product's description.
      var ok = fullL.indexOf(shownL) === 0;
      if (!ok && shownL.length > 20) {
        var trimmed = shownL.replace(/\s+\S*$/, ''); // tolerate a half-cut last word
        ok = !!trimmed && fullL.indexOf(trimmed) === 0;
      }
      if (!ok) return;

      injectReadMore(p, full, normTitle);
    } finally {
      delete p.dataset.keksiRmPending;
    }
  }

  function scan() {
    var descs = document.querySelectorAll(CONFIG.descSelector);
    for (var i = 0; i < descs.length; i++) processOne(descs[i]);
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      scan();
    });
  }

  function start() {
    injectStyles();
    scan();
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) {
          schedule();
          return;
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
